import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '@sentinel/api-client';
import { queryKeys } from '@sentinel/api-client';
import { fetchSession } from '../flows/refresh';
import { startAccessTokenScheduler, type AccessTokenSchedulerOptions } from '../flows/access-token-scheduler';
import { SessionContext, type SessionState } from './hooks';
import { setFederatedUser } from './federated-flag';

export interface SessionProviderProps {
  client: ApiClient;
  children: ReactNode;
  /**
   * Test-only escape hatch for shortening the access-token refresh timing.
   * In production, omit; defaults are 90s lead time and 30s transient retry.
   */
  refreshSchedulerOptions?: AccessTokenSchedulerOptions;
}

const EMPTY_STATE: Omit<SessionState, 'refresh'> = {
  user: null,
  org: null,
  tenant: null,
  roles: [],
  isOrgAdmin: false,
  impersonator: null,
  loading: false,
};

export function SessionProvider({ client, children, refreshSchedulerOptions }: SessionProviderProps) {
  const qc = useQueryClient();
  const [state, setState] = useState<Omit<SessionState, 'refresh'>>({
    ...EMPTY_STATE,
    loading: true,
  });
  const inflight = useRef<Promise<void> | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    if (inflight.current) return inflight.current;
    const p = (async () => {
      setState((s) => ({ ...s, loading: true }));
      try {
        const payload = await fetchSession(client);
        if (payload) {
          setState({
            user: payload.user,
            org: payload.org,
            tenant: payload.tenant,
            roles: payload.roles,
            isOrgAdmin: payload.isOrgAdmin,
            impersonator: payload.impersonator,
            loading: false,
          });
        } else {
          setState(EMPTY_STATE);
        }
        await qc.invalidateQueries({ queryKey: queryKeys.session });
      } catch {
        // A non-401 probe failure (5xx, network/CORS) must still end the
        // loading state — otherwise consumers that gate on `loading` (the
        // portal `/login` loading card, the `/` index redirect) hang forever.
        // Degrade to "unauthenticated, not loading": the login form renders
        // and the user can sign in manually.
        setState(EMPTY_STATE);
      } finally {
        inflight.current = null;
      }
    })();
    inflight.current = p;
    return p;
  }, [client, qc]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!state.user) return;
    const handle = startAccessTokenScheduler(client, refreshSchedulerOptions);
    return () => handle.stop();
  }, [client, state.user?.id, refreshSchedulerOptions]);

  useEffect(() => {
    function onUnauth() {
      setState(EMPTY_STATE);
    }
    window.addEventListener('sentinel:unauthenticated', onUnauth);
    return () => window.removeEventListener('sentinel:unauthenticated', onUnauth);
  }, []);

  // Mirror the current user's federated flag into module state so the
  // api-client step-up middleware can read it lazily at 403 time (it has no
  // access to React context). Cleared to false when there is no user.
  useEffect(() => {
    setFederatedUser(state.user?.localAuthManagedByIdp === true);
  }, [state.user?.localAuthManagedByIdp]);

  const value = useMemo<SessionState>(() => ({ ...state, refresh }), [state, refresh]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
